import React from 'react'

import { PointerWrapper } from '@components/PointerWrapper'
import Box from '@mui/material/Box'
import FormGroup from '@mui/material/FormGroup'
import FormLabel from '@mui/material/FormLabel'
import Typography from '@mui/material/Typography'
import { numberWithCommas } from '@shared/utils'

import {
  FilterExpandMoreIcon,
  MultiLevelSelectCheckbox,
  MultiLevelSelectSummary,
  MultiLevelSelectWrap,
  MultiSelectNestedWrap,
  SelectCheckbox,
  SelectFormControlLabel,
  SelectLabelText,
  SelectLabelWrapper,
} from '../../index.styles'
import MultiSelect from './MultiSelect'

interface Props {
  values: string[]
  label: string
  options: { label: string; value: string; total: string; options?: { label: string; total: string; value: string }[] }[]
  onChange: (value: string) => void
  handleSelectExpand: (e: React.MouseEvent<HTMLElement>, value: boolean, nestedName: string) => void
  expandedFilter: Record<string, boolean | string>
  filterName: string
}

const MultiLevelSelect = (props: Props) => {
  const { values: selectedValues, label, options, onChange, handleSelectExpand, expandedFilter, filterName } = props

  return (
    <FormGroup data-testid="multi_level_select">
      <FormLabel>{label}</FormLabel>
      {options.map(i => {
        const isChecked = selectedValues.includes(i.value)
        const isExpanded = expandedFilter?.nestedExpandedFilter === `${filterName}:${i.value}`
        const handleChange = () => onChange(i.value)
        const onExpand = (e: React.MouseEvent<HTMLElement>) => {
          handleSelectExpand(e, !isExpanded, `${filterName}:${i.value}`)
          e.preventDefault()
        }
        return (
          <MultiLevelSelectWrap key={i.value}>
            {i.options?.length ? (
              <Box>
                <PointerWrapper>
                  <MultiLevelSelectSummary>
                    <FilterExpandMoreIcon onClick={onExpand} opened={isExpanded} />
                    <SelectFormControlLabel
                      key={i.value}
                      control={
                        <MultiLevelSelectCheckbox
                          disableRipple
                          checked={isChecked}
                          onChange={handleChange}
                          inputProps={{ 'aria-label': 'controlled' }}
                        />
                      }
                      label={
                        <SelectLabelWrapper>
                          <SelectLabelText variant="body2" onClick={onExpand}>
                            {i.label}
                          </SelectLabelText>
                          {!!i.total && <Typography variant="body2">({numberWithCommas(i.total)})</Typography>}
                        </SelectLabelWrapper>
                      }
                    />
                  </MultiLevelSelectSummary>
                </PointerWrapper>

                {isExpanded && (
                  <MultiSelectNestedWrap>
                    <MultiSelect
                      values={selectedValues.map(v => v.split('::')[1])}
                      label=""
                      options={i.options}
                      onChange={(v: string) => {
                        return onChange(`${i.value}::${v}`)
                      }}
                    />
                  </MultiSelectNestedWrap>
                )}
              </Box>
            ) : (
              <SelectFormControlLabel
                key={i.value}
                control={
                  <SelectCheckbox checked={isChecked} onChange={handleChange} inputProps={{ 'aria-label': 'controlled' }} />
                }
                label={
                  <SelectLabelWrapper>
                    <Typography variant="body2">{i.label}</Typography>
                    {!!i.total && <Typography variant="body2">({numberWithCommas(i.total)})</Typography>}
                  </SelectLabelWrapper>
                }
              />
            )}
          </MultiLevelSelectWrap>
        )
      })}
    </FormGroup>
  )
}

export default MultiLevelSelect
